// @flow
import * as React from 'react';
import classnames from 'classnames';
import { listCardFactory } from 'common/components/core/card/ListCard';
import Icon, { ErrorIcon } from 'common/components/core/icon';
import { themr } from 'react-css-themr';
import theme from './EntityUploadInfo.scss';
import { translate } from 'react-i18next';
import { DownloadButton } from 'common/components/core/button';
import isString from 'lodash/isString';
import Loader from 'common/components/core/loader/Loader';

const Subtitle = ({ subtitleIcon, subtitle, theme }) => {
    return subtitle ? (
        <div className={theme.subtitleWrap}>
            {isString(subtitleIcon) ? (
                <Icon
                    icon={subtitleIcon}
                    className={theme.subtitleIcon}
                    theme={theme}
                />
            ) : (
                subtitleIcon
            )}
            <div className={theme.subtitle}>{subtitle}</div>
        </div>
    ) : null;
};

const ListCard = listCardFactory({
    Subtitle
});

class EntityUploadInfo extends React.PureComponent {
    getExtraContent = () => {
        const {
            isUploadSuccessful,
            isFileBasedEntity,
            entityNameId,
            t,
            verifiedRowIds,
            parentEntityNameId,
            entitiesToUpload
        } = this.props;

        const verifiedExistingEntitiesSize = entitiesToUpload.filter(entity =>
            verifiedRowIds.includes(entity.id)
        ).size;
        const newEntitiesSize =
            entitiesToUpload.size - verifiedExistingEntitiesSize;

        if (!isFileBasedEntity) {
            if (!isUploadSuccessful) {
                return t('entityImport.insertEntitiesMessage', {
                    entityNameId,
                    entityCount: newEntitiesSize
                });
            }
            return t('entityImport.insertedEntitiesMessage', {
                entityNameId,
                count: newEntitiesSize
            });
        }

        if (!isUploadSuccessful) {
            return t('entityImport.insertFileBasedEntityMessage', {
                entityNameId,
                parentEntityNameId,
                entityCount: newEntitiesSize
            });
        }
        return t('entityImport.insertedFileBasedEntityMessage', {
            entityNameId,
            parentEntityNameId,
            count: newEntitiesSize
        });
    };

    getContentMessage = ({ entityNameId, entityCount }) => {
        const { isUploadSuccessful, doNotAllowNewEntry, t } = this.props;
        if (!isUploadSuccessful) {
            if (doNotAllowNewEntry) {
                return t('entityImport.updateEntitiesMessageWithoutMaster', {
                    entityNameId,
                    entityCount
                });
            }
            return t('entityImport.updateEntitiesMessage', {
                entityNameId,
                entityCount
            });
        }
        return t('entityImport.updatedEntitiesMessage', {
            entityNameId,
            count: entityCount
        });
    };

    render() {
        const {
            verifiedRowIds,
            ignoredRowIds,
            entityNameId,
            t,
            theme,
            isUploadSuccessful,
            isUploadInProgress,
            onDownloadIgnoredEntities,
            onDownloadEntitiesWithErrors,
            isFileBasedEntity,
            entitiesToUpload,
            entitiesWithErrors,
            isFetchSummaryDataInProgress,
            reservedEntitiesTable,
            onDownloadReservedEntities,
            conflictEntities = t('entityImport.otherEntity'),
            doNotAllowNewEntry
        } = this.props;

        if (isUploadInProgress || isFetchSummaryDataInProgress) {
            let config = {};
            if (isUploadInProgress) {
                config = {
                    withText: true,
                    title: t('translations:uploading')
                };
            }
            return <Loader theme={theme} active {...config} />;
        }

        const verifiedExistingEntitiesSize = entitiesToUpload.filter(entity =>
            verifiedRowIds.includes(entity.id)
        ).size;
        const ignoredExisitngEntitiesSize = ignoredRowIds.length;
        const newEntitiesSize =
            entitiesToUpload.size - verifiedExistingEntitiesSize;
        return (
            <div
                className={classnames(theme.entityUploadInfoWrap, {
                    [theme.isUploadSuccessful]: isUploadSuccessful
                })}
            >
                <div className={theme.uploadTitle}>
                    {isUploadSuccessful
                        ? t('entityImport.importSuccess')
                        : t('entityImport.clickingOnNext')}
                </div>
                <div className={theme.entityUploadInfo}>
                    {!doNotAllowNewEntry && (
                        <ListCard
                            theme={theme}
                            title={`${newEntitiesSize}`}
                            subtitle={t('new')}
                            subtitleIcon="plus-thin"
                            extraContent={this.getExtraContent()}
                        />
                    )}
                    {!isFileBasedEntity && (
                        <ListCard
                            theme={theme}
                            title={`${verifiedExistingEntitiesSize}`}
                            subtitle={t('updates')}
                            subtitleIcon="update-icon"
                            extraContent={this.getContentMessage({
                                entityNameId,
                                entityCount: verifiedExistingEntitiesSize
                            })}
                        />
                    )}
                </div>
                <div className={theme.entityUploadInfoBottom}>
                    {ignoredExisitngEntitiesSize > 0 ? (
                        <div className={theme.downloadWrap}>
                            <div className={theme.downloadText}>
                                {isUploadSuccessful
                                    ? t(
                                          'entityImport.existingEntitiesIgnoredMessage',
                                          {
                                              count: ignoredExisitngEntitiesSize,
                                              entityNameId
                                          }
                                      )
                                    : t(
                                          'entityImport.existingEntitiesWillBeIgnoredMessage',
                                          {
                                              count: ignoredExisitngEntitiesSize,
                                              entityNameId
                                          }
                                      )}
                            </div>
                            <DownloadButton
                                theme={theme}
                                label={t('entityImport.downloadList')}
                                onDownload={onDownloadIgnoredEntities}
                            />
                        </div>
                    ) : null}
                    {entitiesWithErrors.size > 0 ? (
                        <div
                            className={classnames(
                                theme.downloadWrap,
                                theme.errorDownloadWrap
                            )}
                        >
                            <ErrorIcon visible theme={theme} />
                            <div className={theme.downloadText}>
                                {isUploadSuccessful
                                    ? t(
                                          'entityImport.entitiesDiscardedMessage',
                                          {
                                              count: entitiesWithErrors.size,
                                              entityNameId
                                          }
                                      )
                                    : t(
                                          'entityImport.entitiesWillBeDiscardedMessage',
                                          {
                                              count: entitiesWithErrors.size,
                                              entityNameId
                                          }
                                      )}
                            </div>
                            <DownloadButton
                                theme={theme}
                                label={t('entityImport.downloadList')}
                                onDownload={onDownloadEntitiesWithErrors}
                            />
                        </div>
                    ) : null}
                    {reservedEntitiesTable?.rows?.size > 0 ? (
                        <div className={classnames(theme.downloadWrap)}>
                            <div className={theme.downloadText}>
                                {isUploadSuccessful
                                    ? t(
                                          'entityImport.entitiesReservedMessage',
                                          {
                                              count:
                                                  reservedEntitiesTable.rows
                                                      .size,
                                              entityNameId,
                                              conflictEntities
                                          }
                                      )
                                    : t(
                                          'entityImport.entitiesReservedMessage',
                                          {
                                              count:
                                                  reservedEntitiesTable.rows
                                                      .size,
                                              entityNameId,
                                              conflictEntities
                                          }
                                      )}
                            </div>
                            <DownloadButton
                                theme={theme}
                                label={t('entityImport.downloadList')}
                                onDownload={onDownloadReservedEntities}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
}
export default translate('entityManagement')(
    themr('EntityUploadInfo', theme)(EntityUploadInfo)
);
